import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { sermonsApi } from '@/lib/api';
import { Play, Calendar, User, Clock, ArrowLeft, Share2 } from 'lucide-react';
import { toast } from 'sonner';

const SermonDetailPage = () => {
  const { id } = useParams();
  const [sermon, setSermon] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSermon();
  }, [id]);

  const loadSermon = async () => {
    try {
      const data = await sermonsApi.getOne(id);
      setSermon(data);
    } catch (error) {
      console.error('Error loading sermon:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const year = date.getFullYear();
    const month = date.toLocaleDateString('en-US', { month: 'long' });
    const day = date.getDate();
    return `${year}, ${month} ${day}`;
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: sermon.title,
        text: `Watch "${sermon.title}" from St. John's UCC`,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    }
  };

  const getVideoEmbed = () => {
    if (sermon.video_url) {
      const url = sermon.video_url;
      
      // YouTube - embed directly on site
      if (url.includes('youtube.com') || url.includes('youtu.be')) {
        let videoId = '';
        if (url.includes('youtube.com/watch')) {
          videoId = new URL(url).searchParams.get('v');
        } else if (url.includes('youtu.be')) {
          videoId = url.split('/').pop().split('?')[0];
        } else if (url.includes('youtube.com/embed')) {
          videoId = url.split('/embed/')[1].split('?')[0];
        }
        
        if (videoId) {
          return (
            <iframe
              src={`https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`}
              className="w-full aspect-video"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title={sermon.title}
            />
          );
        }
      }
      
      // Facebook - embed directly on site
      if (url.includes('facebook.com')) {
        return (
          <iframe
            src={`https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&show_text=false&width=734`}
            className="w-full aspect-video"
            style={{ border: 'none', overflow: 'hidden' }}
            scrolling="no"
            frameBorder="0"
            allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
            allowFullScreen
            title={sermon.title}
          />
        );
      }
      
      // Vimeo
      if (url.includes('vimeo.com')) {
        const vimeoId = url.split('/').pop().split('?')[0];
        return (
          <iframe
            src={`https://player.vimeo.com/video/${vimeoId}`}
            className="w-full aspect-video"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            title={sermon.title}
          />
        );
      }
      
      // Local uploaded video (from /api/uploads/)
      if (url.startsWith('/api/uploads/') || url.includes('/api/uploads/')) {
        const fullUrl = url.startsWith('http') ? url : `${window.location.origin}${url}`;
        return (
          <video
            controls
            className="w-full aspect-video bg-black"
            poster={sermon.thumbnail_url}
          >
            <source src={fullUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        );
      }
      
      // Default: show external link button
      return (
        <div className="w-full aspect-video bg-stone-900 flex items-center justify-center">
          <a href={url} target="_blank" rel="noopener noreferrer">
            <Button className="bg-amber-700 hover:bg-amber-800">
              <Play className="mr-2 h-5 w-5" />
              Watch Video
            </Button>
          </a>
        </div>
      );
    }
    
    // Legacy: base64 video data
    if (sermon.video_data) {
      return (
        <video
          controls
          className="w-full aspect-video bg-black"
          poster={sermon.thumbnail_data || sermon.thumbnail_url}
        >
          <source src={sermon.video_data} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      );
    }
    
    // No video
    return (
      <div className="w-full aspect-video bg-stone-200 flex items-center justify-center">
        <p className="text-stone-500">Video not available</p>
      </div>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-[50vh] flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-700 mx-auto"></div>
            <p className="mt-4 text-stone-600">Loading sermon...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!sermon) {
    return (
      <Layout>
        <div className="min-h-[50vh] flex items-center justify-center">
          <Card className="max-w-md">
            <CardContent className="p-8 text-center">
              <h2 className="text-xl font-semibold text-stone-800 mb-4">Sermon Not Found</h2>
              <Link to="/sermons">
                <Button>
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Sermons
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="bg-black">
        <div className="max-w-5xl mx-auto">
          {getVideoEmbed()}
        </div>
      </div>

      <section className="py-8 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <Link to="/sermons" className="inline-flex items-center text-amber-700 hover:text-amber-800 mb-6">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Sermons
          </Link>

          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="text-3xl font-serif font-bold text-stone-800 mb-4">
                {sermon.title}
              </h1>
              <div className="flex flex-wrap gap-4 text-stone-600">
                <span className="flex items-center">
                  <Calendar className="h-4 w-4 mr-2 text-amber-700" />
                  {formatDate(sermon.date)}
                </span>
                <span className="flex items-center">
                  <User className="h-4 w-4 mr-2 text-amber-700" />
                  {sermon.speaker}
                </span>
                {sermon.duration && (
                  <span className="flex items-center">
                    <Clock className="h-4 w-4 mr-2 text-amber-700" />
                    {sermon.duration}
                  </span>
                )}
              </div>
            </div>
            <Button variant="outline" onClick={handleShare}>
              <Share2 className="mr-2 h-4 w-4" />
              Share
            </Button>
          </div>

          {sermon.description && (
            <div className="mt-8 pt-8 border-t">
              <h2 className="text-xl font-semibold text-stone-800 mb-4">About This Sermon</h2>
              <p className="text-stone-600 whitespace-pre-line">{sermon.description}</p>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default SermonDetailPage;
